import { Box, Typography } from '@mui/material';
import { useLocation } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { usePageHeaderState } from '../../app/pageHeader';
import { resolveSaTrail } from './saNav';

/**
 * The console's per-page header (prototype `SaPageHeader`): a DecisionPoint-rooted
 * breadcrumb, the big page title + subtitle, and the page's right-aligned
 * actions. Title / subtitle / actions come from the shared page-header store
 * (each console page declares them via `<PageHeader/>` under the shell's
 * `InShellContext`); the breadcrumb is derived from the route via `resolveSaTrail`.
 */
export function SaPageHeader() {
  const { t } = useTranslation();
  const location = useLocation();
  const header = usePageHeaderState();

  const crumbs = [
    t('siteAdmin:shell.wordmark'),
    ...resolveSaTrail(location.pathname).map((key) => t(key)),
  ];
  const title = header?.title ?? crumbs[crumbs.length - 1] ?? '';

  return (
    <Box
      sx={{
        display: 'flex',
        alignItems: 'flex-start',
        gap: 2,
        px: { xs: 2, md: '28px' },
        py: '20px',
        bgcolor: 'background.paper',
        borderBottom: '1px solid',
        borderColor: 'divider',
      }}
    >
      <Box sx={{ flex: 1, minWidth: 0 }}>
        <Box
          component="nav"
          aria-label="breadcrumb"
          sx={{ display: 'flex', alignItems: 'center', flexWrap: 'wrap', gap: 0.5 }}
        >
          {crumbs.map((crumb, index) => {
            const last = index === crumbs.length - 1;
            return (
              <Box
                key={`${crumb}-${index}`}
                sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}
              >
                {index > 0 && (
                  <Box
                    component="span"
                    sx={{ color: 'text.secondary', opacity: 0.5, fontSize: 12 }}
                  >
                    /
                  </Box>
                )}
                <Typography
                  component="span"
                  sx={{
                    fontFamily: (theme) => theme.typography.button.fontFamily,
                    fontSize: 12,
                    lineHeight: 1.4,
                    letterSpacing: '0.5px',
                    textTransform: 'uppercase',
                    fontWeight: last ? 700 : 500,
                    color: last ? 'text.primary' : 'text.secondary',
                  }}
                >
                  {crumb}
                </Typography>
              </Box>
            );
          })}
        </Box>

        <Typography
          component="h1"
          sx={{
            mt: '8px',
            fontFamily: (theme) => theme.typography.button.fontFamily,
            fontWeight: 700,
            fontSize: 23,
            lineHeight: 1.2,
            letterSpacing: '-0.01em',
            color: 'text.primary',
          }}
        >
          {title}
        </Typography>
        {header?.subtitle && (
          <Typography variant="body2" sx={{ mt: '4px', fontWeight: 400, color: 'text.secondary' }}>
            {header.subtitle}
          </Typography>
        )}
      </Box>

      {header?.actions && (
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.25, flexShrink: 0 }}>
          {header.actions}
        </Box>
      )}
    </Box>
  );
}
